/**
 * The words and dates of the case view.
 *
 * MyWorkView grew to 3,600 lines, and every panel inside it — the ticket list,
 * the detail, the calendar, the notes — spoke through the same handful of tiny
 * helpers: the three-language switch, the two date formats, the stage names
 * that HubSpot writes far too long.
 *
 * They live here so a panel can move into its own file without dragging them
 * along, or importing them back from the 3,600-line file, which is the
 * circular dependency that makes a refactor rot. This module imports from
 * nobody but the language type.
 */
import { personName } from '@/lib/people';
import type { Lang } from '@/data/types';
import { COUNTRIES } from '@/data';

/** A country's flag, and a colleague's name — the two lookups every panel does.
 *  The flag from the portal's list of countries (all six): it read the mock
 *  board's list of three, so an Italian, Greek or Mexican case had none. */
export const flagOf = (code: string) => COUNTRIES.find((c) => c.code === code)?.flag || '';
export const nameOfWorker = (id: string | null) => { const n = personName(id); return n === '—' ? '' : n; };

/** The portal's three-language switch, in one line. Portuguese reads English. */
export const T = (lang: Lang, en: string, es: string, fr: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : en;

export function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}
/** Date with the year — used where the year actually matters (deals, history). */
export function fmtDateY(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
/** Whole days between an ISO date and now (0 if today/future or invalid). */
export function daysSince(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

/** A finished ticket (server already excludes these for live data; safety net for mock). */
export function isCompletedStage(label: string): boolean {
  return /^(complete|completed|cancelled|canceled|closed|resolved)$/i.test((label || '').trim());
}
/** A paused ticket — surfaced with an orange marker. */
export function isOnHold(label: string): boolean {
  return /on[\s-]?hold|en espera|en pausa|standby/i.test(label || '');
}
/** Short, clean pipeline label for the filter tabs (no AI — just tidy text). */
export function prettyPipeline(label: string, lang: Lang): string {
  if (/nationalit|nacionalidad|citizenship/i.test(label)) return T(lang, 'Nationality', 'Nacionalidad', 'Nationalité');
  if (/immigration|inmigra/i.test(label)) return T(lang, 'Immigration', 'Inmigración', 'Immigration');
  return label.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
}
/** Short, readable stage name for the Stage filter (raw labels are very long). */
export function shortStage(label: string): string {
  /* «PT - Bank Account» and «Bank Account» are ONE stage to the person filtering:
     the country prefix some pipelines put on their stages listed every stage
     twice in Portugal's filter (Gerónimo, 28 Oct 2026: «¿por qué tantos?»). */
  let s = (label || '').replace(/^\s*(it|pt|fr|es|sp|gr|mx)\s*[-–—]\s*/i, '')
    .replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const abbr: [RegExp, string][] = [
    [/gathering and review of documents/i, 'Docs review'],
    [/list of documents( sent)?/i, 'Docs list'],
    [/additional documentation required/i, 'Docs required'],
    [/waiting (on|for) resol/i, 'Awaiting resolution'],
    [/appo.*consulate.*submission|submission/i, 'Submission'],
    [/biometrics appointment/i, 'Biometrics'],
    [/fingerprints appointment/i, 'Fingerprints'],
    [/waiting for card collection/i, 'Card collection'],
    [/previous formalities/i, 'Previous formalities'],
    [/on[\s-]?hold/i, 'On hold'],
  ];
  for (const [re, short] of abbr) if (re.test(s)) return short;
  if (s.length > 24) s = `${s.slice(0, 22).trim()}…`;
  return s;
}

/** "JS" from "Jules Silva" — the avatar when there is no photo. */
export const initials = (name: string) =>
  name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();


/** "3d ago" — recency where the exact minute does not matter. */
export function timeAgo(iso: string | null, lang: Lang): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return T(lang, `${mins}m ago`, `hace ${mins}m`, `il y a ${mins}m`);
  const h = Math.floor(mins / 60);
  if (h < 24) return T(lang, `${h}h ago`, `hace ${h}h`, `il y a ${h}h`);
  const d = Math.floor(h / 24);
  return T(lang, `${d}d ago`, `hace ${d}d`, `il y a ${d}j`);
}
